import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Clock, XCircle } from "lucide-react";

const ASSESS = [
  { key: "on_time", label: "On time", icon: CheckCircle2, active: "bg-emerald-600 text-white", idle: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" },
  { key: "late", label: "Late", icon: Clock, active: "bg-amber-500 text-white", idle: "bg-amber-50 text-amber-700 hover:bg-amber-100" },
  { key: "undone", label: "Undone", icon: XCircle, active: "bg-rose-600 text-white", idle: "bg-rose-50 text-rose-700 hover:bg-rose-100" },
];

export const HomeworkSubmissionsPanel = ({ hwId }) => {
  const [rows, setRows] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/teacher/homework/${hwId}/submissions`);
      setRows(data.rows);
      const d = {};
      data.rows.forEach((r) => { d[r.student_id] = { assessment: r.assessment, remarks: r.remarks || "" }; });
      setDrafts(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [hwId]); // eslint-disable-line

  const save = async (studentId) => {
    const d = drafts[studentId];
    if (!d?.assessment) { toast.error("Pick On time, Late or Undone first"); return; }
    setSaving(studentId);
    try {
      await api.post(`/teacher/homework/${hwId}/review`, { student_id: studentId, assessment: d.assessment, remarks: d.remarks });
      toast.success("Assessment saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(null);
    }
  };

  if (!rows) return <div className="py-6 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  if (rows.length === 0) return <p className="py-4 text-sm text-slate-500">No students in this batch yet.</p>;

  return (
    <div className="mt-3 border-t border-slate-100 pt-3 space-y-3" data-testid={`hw-submissions-${hwId}`}>
      {rows.map((r) => (
        <div key={r.student_id} className="rounded-xl bg-slate-50 border border-slate-100 p-3" data-testid={`hw-sub-row-${r.student_id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <span className="font-semibold text-slate-900 text-sm">{r.name}</span>
              {r.roll_number && <span className="ml-2 text-xs text-slate-400">#{r.roll_number}</span>}
              <div className="text-xs mt-0.5">
                {r.marked_done ? (
                  <span className={r.timeliness === "late" ? "text-amber-600 font-semibold" : "text-emerald-600 font-semibold"}>
                    Student marked done {r.timeliness === "late" ? "(after due date)" : "(on time)"}
                    {r.marked_done_at && <span className="text-slate-400 font-normal"> · {new Date(r.marked_done_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>}
                  </span>
                ) : (
                  <span className="text-slate-400">Not marked done by student</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {ASSESS.map((a) => {
                const sel = drafts[r.student_id]?.assessment === a.key;
                return (
                  <button key={a.key} type="button"
                    data-testid={`assess-${a.key}-${r.student_id}`}
                    onClick={() => setDrafts((d) => ({ ...d, [r.student_id]: { ...d[r.student_id], assessment: a.key } }))}
                    className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-bold transition-colors ${sel ? a.active : a.idle}`}>
                    <a.icon className="h-3.5 w-3.5" /> {a.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="mt-2 flex gap-2">
            <input
              value={drafts[r.student_id]?.remarks || ""}
              onChange={(e) => setDrafts((d) => ({ ...d, [r.student_id]: { ...d[r.student_id], remarks: e.target.value } }))}
              placeholder="Remarks for the student (optional)"
              data-testid={`hw-remarks-${r.student_id}`}
              className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-blue-500 outline-none bg-white"
            />
            <button type="button" onClick={() => save(r.student_id)} disabled={saving === r.student_id}
              data-testid={`hw-save-review-${r.student_id}`}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60">
              {saving === r.student_id ? "..." : r.assessment ? "Update" : "Save"}
            </button>
          </div>
          {r.reviewed_at && (
            <p className="mt-1.5 text-[11px] text-slate-400">Last reviewed {new Date(r.reviewed_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</p>
          )}
        </div>
      ))}
    </div>
  );
};
